import apiConfig from '../../../shared/config/apiConfig'
import { createBrandApiRequest } from '../../../shared/brand/utils/brandApiUtils'
import { SECTION_WRITE_BATCH_LIMIT } from '../config/draftReviewConfig'
import { mapBrandProfile } from '../mappers/brandProfileMapper'
import {
  mapBrandDetails,
  mapDraftReview,
  mapExtractionStart,
  mapExtractionStatus,
  mapSectionWrite,
  toSectionWriteBody,
} from '../mappers/draftReviewMapper'

/*
 * The four endpoints behind extraction and review. Each is keyed on the brand
 * uuid alone — the backend resolves it to the brand's latest non-cancelled
 * extraction job, so nothing has to be held across a session boundary.
 * See "Extraction - API Contract" on Confluence (CONTENT/1365937796).
 */
const BRAND_UUID_TOKEN = '{brandUuid}'

// Starting extraction also validates every document URL on the server, which
// can take longer than the client's default timeout.
const EXTRACTION_TRIGGER_TIMEOUT_MS = 20000

// A configured endpoint without the token still works: the uuid is appended.
const brandPath = (template = '', brandUuid) => {
  const uuid = encodeURIComponent(brandUuid)
  return template.includes(BRAND_UUID_TOKEN)
    ? template.replace(BRAND_UUID_TOKEN, uuid)
    : `${template}/${uuid}`
}

const getBrandUuid = (brand) => brand?.id || ''

export const createBrandExtractionService = ({
  brandRequest,
  endpoints = apiConfig.brandAPIs,
} = {}) => {
  const request =
    brandRequest || createBrandApiRequest({ apiKey: apiConfig.apiKey || '' })

  const call = (template, brand, options = {}) =>
    request({ endpoint: brandPath(template, getBrandUuid(brand)), ...options })

  return {
    // Kicks extraction off for every uploaded document on the brand. The API
    // validates the documents first and rejects the call if one is unreachable
    // (EXTRACTION_DOCUMENT_URL_UNREACHABLE). What happens next is learnt only by polling getExtractionStatus.
    startExtraction: async (brand) =>
      mapExtractionStart(
        await call(endpoints?.EXTRACTION_TRIGGER, brand, {
          method: 'post',
          timeout: EXTRACTION_TRIGGER_TIMEOUT_MS,
        }),
      ),

    // Re-runs a failed job in place, instead of starting a new one.
    retryExtraction: async (brand, jobId) =>
      mapExtractionStart(
        await call(
          endpoints?.EXTRACTION_RETRY?.replace(
            '{extractionJobId}',
            encodeURIComponent(jobId),
          ),
          brand,
          { method: 'post', timeout: EXTRACTION_TRIGGER_TIMEOUT_MS },
        ),
      ),

    // The poll target. Safe to call for a brand that has never extracted —
    // it answers 200 with status NOT_STARTED.
    getExtractionStatus: async (brand) =>
      mapExtractionStatus(await call(endpoints?.EXTRACTION_STATUS, brand)),

    // Worth calling only once the status endpoint reports review_ready.
    getDraftReview: async (brand) =>
      mapDraftReview(await call(endpoints?.BRAND_SECTIONS, brand)),

    // The brand's lifecycle and ingestion statuses.
    getDetails: async (brand) =>
      mapBrandDetails(await call(endpoints?.BRAND_DETAILS, brand)),

    // The published profile shown on the Brand Profile tab.
    getProfile: async (brand) =>
      mapBrandProfile(await call(endpoints?.BRAND_PROFILE, brand)),

    // Only the sections listed change; every other one carries forward.
    // `sections` is [{ id, value }].
    updateProfile: (brand, sections) =>
      call(endpoints?.BRAND_PROFILE, brand, {
        body: {
          sections: sections.map(({ id, value }) => ({
            section_uuid: id,
            section_value: value,
          })),
        },
        method: 'patch',
      }),

    // The brand's latest publication: its status, its ID and whether a
    // failed one can still be retried. Answers 404 if it was never sent.
    getCurrentPublication: async (brand) =>
      call(endpoints?.BRAND_PUBLICATION_CURRENT, brand),

    // One call covers one decision or a whole group of them, as a single
    // transaction. The response carries the updated sections and refreshed
    // counters, so the caller never has to re-read the review to stay in step.
    //
    // Over the limit this refuses rather than splitting: two calls would be
    // two transactions, and a group approve that half-applied is exactly what
    // the all-or-nothing rule exists to prevent.
    saveSectionDecisions: async (brand, decisions = []) => {
      if (decisions.length > SECTION_WRITE_BATCH_LIMIT) {
        throw new Error(
          `Only ${SECTION_WRITE_BATCH_LIMIT} sections can be saved at once.`,
        )
      }
      return mapSectionWrite(
        await call(endpoints?.BRAND_SECTIONS, brand, {
          body: toSectionWriteBody(decisions),
          method: 'put',
        }),
      )
    },

    // Creates the publication that enters the brand into the publish queue.
    sendToPublishQueue: async (brand) => {
      return call(endpoints?.BRAND_PUBLISH, brand, { method: 'post' })
    },

    // Only works while the publication's undo window is still open.
    cancelPublication: (brand, publicationId) =>
      call(
        endpoints?.BRAND_PUBLICATION_CANCEL?.replace(
          '{publicationId}',
          encodeURIComponent(publicationId),
        ),
        brand,
        { method: 'post' },
      ),

    // For a publication that has run out of retries.
    recoverPublication: (brand, publicationId) =>
      call(
        endpoints?.BRAND_PUBLICATION_RECOVER?.replace(
          '{publicationId}',
          encodeURIComponent(publicationId),
        ),
        brand,
        { method: 'post' },
      ),

    // Sends a failed publication again, while it still has retries left.
    retryPublication: (brand, publicationId) =>
      call(
        endpoints?.BRAND_PUBLICATION_RETRY?.replace(
          '{publicationId}',
          encodeURIComponent(publicationId),
        ),
        brand,
        { method: 'post' },
      ),
  }
}

export const brandExtractionService = createBrandExtractionService()
